// Search bar component
import { useState, useEffect } from 'react';
import { Search, X } from 'lucide-react';
import { cn } from '@utils/classNames';

interface SearchBarProps {
  placeholder?: string;
  onSearch?: (query: string) => void;
  onClear?: () => void;
  className?: string;
  value?: string;
  debounceMs?: number;
  /** Shows spinner / pulse while server search is running */
  isSearching?: boolean;
}

export const SearchBar: React.FC<SearchBarProps> = ({
  placeholder = 'Search files...',
  onSearch,
  onClear,
  className,
  value: controlledValue,
  debounceMs = 300,
  isSearching = false,
}) => {
  const [query, setQuery] = useState(controlledValue || '');
  // Sync with controlled value
  useEffect(() => {
    if (controlledValue !== undefined) {
      setQuery(controlledValue);
    }
  }, [controlledValue]);

  // Only animate while the API search runs — avoids flicker between debounce and fetch
  const showSearchActivity = isSearching;

  // Debounced search
  useEffect(() => {
    if (!onSearch) return;

    const timer = setTimeout(() => {
      onSearch(query);
    }, debounceMs);

    return () => clearTimeout(timer);
  }, [query, debounceMs, onSearch]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSearch?.(query);
  };

  const handleClear = () => {
    setQuery('');
    onClear?.();
    onSearch?.('');
  };

  return (
    <form
      onSubmit={handleSubmit}
      className={cn('relative w-full', className)}
      aria-busy={showSearchActivity}
    >
      <div
        className={cn(
          'relative w-full h-[32px] xs:h-[34px] sm:h-[36px] md:h-[38px] lg:h-[40px] xl:h-[42px] 2xl:h-[44px] bg-white/10 backdrop-blur-sm rounded-lg sm:rounded-xl border border-white/10',
          showSearchActivity && 'border-primary/45 ring-1 ring-primary/25'
        )}
      >
        {/* Fixed-size slot so swapping spinner ↔ icon never shifts layout */}
        <span
          className="absolute left-2 xs:left-2.5 sm:left-3 md:left-3 lg:left-3.5 top-1/2 -translate-y-1/2 flex size-[18px] sm:size-5 lg:size-[22px] items-center justify-center pointer-events-none shrink-0"
          aria-hidden
        >
          {showSearchActivity ? (
            <span className="box-border size-[14px] sm:size-4 rounded-full border-2 border-primary/25 border-t-primary animate-spin motion-reduce:animate-none [transform-origin:center]" />
          ) : (
            <Search className="size-[14px] sm:size-4 lg:size-[18px] text-white/60" strokeWidth={2.25} />
          )}
        </span>
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={placeholder}
          aria-label={placeholder}
          aria-describedby={showSearchActivity ? 'search-bar-status' : undefined}
          className="absolute top-0 left-0 w-full h-full pl-6 xs:pl-7 sm:pl-8 md:pl-9 lg:pl-10 pr-5 xs:pr-6 sm:pr-7 md:pr-8 lg:pr-9 bg-transparent rounded-lg sm:rounded-xl font-bold text-[10px] xs:text-[11px] sm:text-xs md:text-sm lg:text-base text-white placeholder:text-white/50 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
        />
        {query && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute top-1/2 -translate-y-1/2 right-2 xs:right-2.5 sm:right-3 md:right-3 lg:right-3.5 w-3.5 h-3.5 xs:w-4 xs:h-4 sm:w-4.5 sm:h-4.5 md:w-5 md:h-5 lg:w-5.5 lg:h-5.5 flex items-center justify-center text-white/60 hover:text-white transition-colors touch-manipulation min-w-[28px] min-h-[28px]"
            aria-label="Clear search"
          >
            <X className="w-3 h-3 xs:w-3.5 xs:h-3.5 sm:w-4 sm:h-4 md:w-4.5 md:h-4.5 lg:w-5 lg:h-5" />
          </button>
        )}
      </div>
      <span id="search-bar-status" className="sr-only" aria-live="polite">
        {showSearchActivity ? 'Searching…' : ''}
      </span>
    </form>
  );
};

